/**
 * finance-core — Lãi kép & niên kim (compound interest / annuity)
 *
 * Module thuần hàm: chỉ nhận số, trả về số. Không phụ thuộc React/Firestore.
 * Đây là NGUỒN CHÂN LÝ DUY NHẤT cho các công thức này — mọi màn hình phải
 * import từ đây (qua src/finance hoặc lớp re-export forecastService),
 * không được tự viết lại công thức.
 *
 * Quy ước đơn vị:
 * - Lãi suất truyền vào là %/năm (ví dụ 7 nghĩa là 7%/năm), ghép lãi theo tháng.
 * - Thời gian: years là số năm (có thể lẻ), months là số tháng nguyên.
 */

/** Lãi suất tháng (thập phân) từ lãi suất %/năm, ghép theo tháng. */
export const monthlyRateOf = (annualRatePercent: number): number =>
  annualRatePercent / 12 / 100;

/**
 * Giá trị tương lai của khoản gốc + đóng góp hàng tháng (niên kim cuối kỳ).
 * FV = PV·(1+r)^n + PMT·[((1+r)^n − 1) / r]
 */
export const futureValue = (
  principal: number,
  monthlyContribution: number,
  annualRatePercent: number,
  years: number
): number => {
  const r = monthlyRateOf(annualRatePercent);
  const n = years * 12;

  if (r === 0) {
    return principal + monthlyContribution * n;
  }

  const growth = Math.pow(1 + r, n);
  return principal * growth + monthlyContribution * ((growth - 1) / r);
};

/**
 * Giá trị thực (đã trừ lạm phát) của một khoản tiền danh nghĩa trong tương lai.
 * Real = Nominal / (1 + inflation)^years
 */
export const realValue = (
  nominalValue: number,
  annualInflationPercent: number,
  years: number
): number => nominalValue / Math.pow(1 + annualInflationPercent / 100, years);

/**
 * Số tháng cần thiết để đạt mục tiêu, TÍNH CẢ số dư hiện có làm gốc.
 * Giải n từ: goal = PV·(1+r)^n + PMT·[((1+r)^n − 1)/r]
 * → n = ln((goal·r/PMT + 1) / (PV·r/PMT + 1)) / ln(1+r)
 *
 * Trả về 0 nếu đã đạt mục tiêu; Infinity nếu không bao giờ đạt
 * (không đóng góp, hoặc đóng góp âm/không đủ bù).
 */
export const monthsToReachGoal = (
  currentAmount: number,
  monthlyContribution: number,
  annualRatePercent: number,
  goalAmount: number
): number => {
  if (currentAmount >= goalAmount) return 0;
  if (monthlyContribution <= 0) {
    // Không đóng góp: chỉ đạt được nếu lãi trên số dư hiện có đủ lớn —
    // với PMT = 0 công thức niên kim không áp dụng; xử lý riêng.
    const r = monthlyRateOf(annualRatePercent);
    if (monthlyContribution === 0 && r > 0 && currentAmount > 0) {
      return Math.ceil(Math.log(goalAmount / currentAmount) / Math.log(1 + r));
    }
    return Infinity;
  }

  const r = monthlyRateOf(annualRatePercent);

  if (r === 0) {
    return Math.ceil((goalAmount - currentAmount) / monthlyContribution);
  }

  const numerator = Math.log(
    (goalAmount * r / monthlyContribution + 1) /
    (currentAmount * r / monthlyContribution + 1)
  );
  return Math.ceil(numerator / Math.log(1 + r));
};

/**
 * Khoản đóng góp hàng tháng cần thiết để đạt mục tiêu đúng hạn.
 * PMT = (FV − PV·(1+r)^n) · r / ((1+r)^n − 1)
 * Có thể trả về số âm nếu số dư hiện có tự nó đã vượt mục tiêu khi đến hạn.
 */
export const requiredMonthlySavings = (
  currentAmount: number,
  goalAmount: number,
  annualRatePercent: number,
  years: number
): number => {
  const r = monthlyRateOf(annualRatePercent);
  const n = years * 12;

  if (n <= 0) return goalAmount - currentAmount > 0 ? Infinity : 0;

  if (r === 0) {
    return (goalAmount - currentAmount) / n;
  }

  const growth = Math.pow(1 + r, n);
  return ((goalAmount - currentAmount * growth) * r) / (growth - 1);
};
